import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { format, parse } from 'date-fns';
import { BehaviorSubject, first, map, Observable, of } from 'rxjs';

import {
  timeInputBlurFn,
  timeInputPasteFn,
  timeInputTransformFn,
} from '@blaze-shared/shared-feature';
import { ScrollerComponent } from '@blaze-shared/shared-ui';
import {
  isValidDate,
  Meridian,
  TIME_PROPOSAL_ARRAY,
  timeFormatter,
  timeParsingFormats,
} from '@blaze-shared/shared-util';

import { getTimeValueToDisplay } from '../../../availability.utils';

const INLINE_EDITOR_SELECTED_TIME_PICKER_SELECTOR = '.bd-menu.inline-editor-time-picker .selected';

export interface TimePickerConfig {
  min?: string;
  max?: string;
}

@UntilDestroy()
@Component({
  selector: 'blaze-availability-time-picker',
  templateUrl: './availability-time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatMenuModule, MatDatepickerModule, ScrollerComponent, AsyncPipe],
})
export class AvailabilityTimePickerComponent implements OnInit, OnDestroy {
  injector = inject(Injector);

  config = input<TimePickerConfig>();
  readonly = input<boolean>(false);
  value = input<unknown>();
  pickerClosed = output();
  pickerOpened = output();
  valueChanged = output<string | null>();

  menuTrigger = viewChild('menuTrigger', { read: MatMenuTrigger });
  timeInputRef = viewChild<ElementRef<HTMLInputElement>>('timeInput');

  displayedTimeFormControl: FormControl = new FormControl();

  useTwelveHoursTimeFormat = true;
  timeSuffix: Meridian = '';
  timePlaceholder = '';

  timeProposalArray$: Observable<string[]> = of([]);
  filterString$ = new BehaviorSubject('');
  proposedValuesFromInput: string[] = [];

  ngOnInit(): void {
    effect(
      () => {
        if (!this.value()) {
          this.displayedTimeFormControl.patchValue(null, { emitEvent: false });
        }
      },
      { injector: this.injector },
    );
    this.displayedTimeFormControl = this.createTimeFormControl();
    this.timeProposalArray$ = this.filterString$.pipe(
      map((input) => {
        if (!input) {
          return TIME_PROPOSAL_ARRAY;
        }

        const filter = input.toLowerCase();
        const baseTime = TIME_PROPOSAL_ARRAY.filter((time) =>
          time.toLowerCase().startsWith(filter),
        );
        return baseTime.length ? baseTime : this.proposeValuesFromInput(input);
      }),
    );
  }

  ngOnDestroy(): void {
    this.closeTimePicker();
  }

  selectTimeInput(): void {
    setTimeout(() => {
      this.timeInputRef()?.nativeElement.select();
    });
  }

  /** @param forceDateBlur fixes date input value change after selecting date from calendar */
  openTimePicker(): void {
    const focusInput = !this.menuTrigger()?.menuOpen;

    this.menuTrigger()
      ?.menuOpened.asObservable()
      .pipe(first())
      .subscribe(() => {
        setTimeout(() => {
          document.querySelector(INLINE_EDITOR_SELECTED_TIME_PICKER_SELECTOR)?.scrollIntoView();
        });
      });
    this.menuTrigger()?.openMenu();
    setTimeout(() => {
      this.timeInputRef()?.nativeElement?.focus();
      if (focusInput) {
        this.timeInputRef()?.nativeElement?.select();
      }
      this.pickerOpened.emit();
    });
  }

  closeTimePicker(): void {
    this.menuTrigger()?.closeMenu();
    this.filterString$.next('');
  }

  protected timeInputTransform(event: InputEvent, formControl: FormControl): void {
    timeInputTransformFn(
      event,
      formControl,
      this.useTwelveHoursTimeFormat,
      this.useTwelveHoursTimeFormat,
    );
  }

  protected timeInputKeyDown(event: KeyboardEvent): void {
    // BL-7853 - Temporary solution
    // Pass through only specific events so the FocusKeyManager does not handle every keydown event
    // and take focus, blurring the input in effect.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      return;
    }

    event.stopPropagation();
  }

  protected timeInputPaste(event: ClipboardEvent, formControl: UntypedFormControl): void {
    const changeTimeVariantToPM = timeInputPasteFn(
      event,
      formControl,
      this.useTwelveHoursTimeFormat,
    );
    if (changeTimeVariantToPM) {
      this.timeSuffix = 'PM';
    }
  }

  protected timeInputBlur(event: FocusEvent, formControl: UntypedFormControl): void {
    // omit blur effect when clicked on proposed time from menu
    if ((event.relatedTarget as HTMLElement)?.closest('.inline-editor-time-picker')) {
      return;
    } else {
      this.closeTimePicker();
    }
    timeInputBlurFn(formControl, this.useTwelveHoursTimeFormat);
  }

  protected patchTimeProposal(value: string): void {
    this.displayedTimeFormControl.patchValue(value);
  }

  private proposeValuesFromInput(value: string | undefined): string[] {
    let newVal = value || this.timeInputRef()?.nativeElement?.value || '';
    const formControl = new UntypedFormControl();
    formControl.setValue(newVal);
    timeInputBlurFn(formControl, this.useTwelveHoursTimeFormat);
    if (!formControl.value) {
      return [];
    }
    newVal = formControl.value.replaceAll(/[APM\s]/gim, '');
    const timeWithSuffix = [];
    if (Number(newVal.split(':')[0]) > 12) {
      timeWithSuffix.push(newVal);
    } else {
      timeWithSuffix.push(`${newVal} AM`, `${newVal} PM`);
    }
    return timeWithSuffix;
  }

  private getTimeParts(
    newTime: string | null,
  ): { hours: string; minutes: string; ampm: string } | null {
    if (!newTime) {
      return null;
    }
    let parsed: Date;

    if (this.useTwelveHoursTimeFormat) {
      parsed = parse(
        `${newTime || '00:00 AM'}`,
        timeParsingFormats.timeInputTwelveHoursWithSuffix,
        new Date(),
      );
    } else {
      parsed = parse(`${newTime || '00:00'}`, timeParsingFormats.timeInput, new Date());
    }

    if (!isValidDate(parsed)) {
      const parsedFallbackParts = newTime.split(/:|\s/);
      return {
        hours: parsedFallbackParts[0],
        minutes: parsedFallbackParts[1],
        ampm: parsedFallbackParts[2],
      };
    }

    return {
      hours: format(parsed, 'HH'),
      minutes: format(parsed, 'mm'),
      ampm: this.useTwelveHoursTimeFormat ? format(parsed, 'aa') : '',
    };
  }

  private createTimeFormControl(): FormControl {
    const typeCheckValue = this.value();
    const value = getTimeValueToDisplay(typeCheckValue, this.useTwelveHoursTimeFormat);
    const formControl = new FormControl({
      value: value,
      disabled: false,
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.filterString$.next(value || '');
      const newTimeParts = this.getTimeParts(value);
      if (!newTimeParts) {
        this.valueChanged.emit(null);
        return;
      }
      this.valueChanged.emit(timeFormatter(value, false) ?? null);
    });

    return formControl;
  }
}
